import { mergeRefs } from '@fe-learner-common/utils/merge-refs';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons/faCircleXmark';
import { cn, FaIcon, Spinner } from 'fe-learner-common';
import { FC, forwardRef, Ref, useMemo, useRef } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

type SearchInputProps = {
  className?: string;
  id: string;
  defaultValue?: string;
  onSearch: (value: string) => void;
  inputRef?: Ref<HTMLInputElement>;
  inputClassName?: string;
  isLoading?: boolean;
  texts: {
    placeholder: string;
    clearSearchAriaLabel: string;
    loadingAriaLabel?: string;
  };
};

export const SearchInput: FC<SearchInputProps> = forwardRef<
  HTMLFormElement,
  SearchInputProps
>(
  (
    {
      className,
      id,
      defaultValue,
      texts,
      inputRef,
      inputClassName,
      isLoading,
      onSearch,
    },
    ref,
  ) => {
    const localInputRef = useRef<HTMLInputElement | null>(null);
    const inputRefs = useMemo(
      () => mergeRefs(localInputRef, inputRef),
      [inputRef],
    );

    const onClear = () => {
      onSearch('');
      if (localInputRef.current) {
        localInputRef.current.value = '';
        localInputRef.current.focus();
      }
    };

    const debouncedOnSearch = useDebounceCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onSearch(event.target.value);
      },
      200,
    );

    return (
      <form
        ref={ref}
        className={cn('l-search-form relative', className)}
        onSubmit={(event) => event.preventDefault()}
      >
        <div role="search">
          <label
            className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3"
            htmlFor={id}
            aria-labelledby={`${id}SearchTitle`}
          >
            <FaIcon icon={faMagnifyingGlass} width={16} height={16} />
            <title id={`${id}SearchTitle`}>{texts.placeholder}</title>
          </label>
          <input
            ref={inputRefs}
            defaultValue={defaultValue}
            id={id}
            type="search"
            onChange={debouncedOnSearch}
            placeholder={texts.placeholder}
            className={cn('l-input l-input-md ps-10', inputClassName)}
          />
          <span className="sr-only" aria-live="polite" aria-busy={isLoading}>
            {isLoading ? texts.loadingAriaLabel : ''}
          </span>
          {isLoading ? (
            <span className="pointer-events-none absolute end-2.5 top-1/2 -translate-y-1/2 text-neutral-550">
              <Spinner name={`${id}-spinner`} />
            </span>
          ) : (
            <button
              type="button"
              onClick={onClear}
              className="l-btn l-icon-btn l-btn-secondary l-btn-sm absolute end-0 top-1/2 hidden -translate-y-1/2 text-neutral-550"
              aria-label={texts.clearSearchAriaLabel}
            >
              <FaIcon icon={faCircleXmark} width={16} height={16} />
            </button>
          )}
        </div>
      </form>
    );
  },
);
SearchInput.displayName = 'SearchInput';
